import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import {
  CartEditModalProps,
  CartType,
} from "../../../../redux/cartConfigurationSlice";
import "./index.css";
import {
  useLazyGetCartTypesQuery,
  useUpdateCartLoadingConfigurationMutation,
} from "../../../../redux/services/DistributionService";
import { toast } from "react-toastify";

const CartEditModal: React.FC<CartEditModalProps> = ({
  cart,
  onClose,
  onSave,
  availableGates,
}) => {
  const [updateCartLoading, { isLoading }] =
    useUpdateCartLoadingConfigurationMutation();

  const [getCartTypes, { data: cartTypesData }] = useLazyGetCartTypesQuery();
  const [cartTypes, setCartTypes] = useState<CartType[]>([]);

  // Fetch cart types on component mount
  useEffect(() => {
    getCartTypes();
  }, [getCartTypes]);

  // Update cart types when data is available
  useEffect(() => {
    if (cartTypesData?.cartTypesList) {
      setCartTypes(cartTypesData.cartTypesList);
    }
  }, [cartTypesData]);

  const formatTimeOnly = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  const [formData, setFormData] = useState({
    priority: cart.priority || "",
    leaveTime: formatTimeOnly(cart.leaveTime),
    assignedGate: cart.assignedGate || "",
    cartType: cart.cartType || "",
    notes: cart.notes || "",
  });

  const handleSave = async () => {
    try {
      let leaveTimeDate = null;

      if (formData.leaveTime) {
        // Get current date in Indian timezone (IST - UTC+5:30)
        const now = new Date();
        const [hours, minutes] = formData.leaveTime.split(":");

        // Create date with current day and selected time
        const localDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          parseInt(hours),
          parseInt(minutes)
        );

        // Format as "2025-09-22T12:01:00+05:30"
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, "0");
        const day = String(localDate.getDate()).padStart(2, "0");
        const formattedHours = String(localDate.getHours()).padStart(2, "0");
        const formattedMinutes = String(localDate.getMinutes()).padStart(
          2,
          "0"
        );

        // Create the exact format you requested
        leaveTimeDate = `${year}-${month}-${day}T${formattedHours}:${formattedMinutes}:00+05:30`;
      }

      const apiData = {
        cartId: cart.id,
        priority: formData.priority || null,
        leaveTime: leaveTimeDate,
        assignedGate: formData.assignedGate || null,
        cartType: formData.cartType || null,
      };

      await updateCartLoading(apiData).unwrap();

      const updates = {
        priority: formData.priority || null,
        leaveTime: leaveTimeDate,
        assignedGate: formData.assignedGate || null,
        cartType: formData.cartType || null,
        notes: formData.notes || undefined,
      };

      onSave(cart.id, updates);
      onClose();
      toast.success("Cart configuration updated successfully");
    } catch (error) {
      toast.error("Failed to update cart configuration");
    }
  };
  return (
    <div className="cart-configuration-edit-modal-overlay">
      <div className="cart-configuration-edit-modal">
        <div className="cart-configuration-edit-modal-header">
          <h3>Edit Cart Configuration - {cart.displayName || cart.id}</h3>
          <button
            onClick={onClose}
            className="cart-configuration-edit-modal-close"
          >
            ×
          </button>
        </div>

        <div className="cart-configuration-edit-modal-content">
          <div className="cart-configuration-edit-modal-form-group">
            <label>Priority Level</label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, priority: e.target.value }))
              }
            >
              <option value="">Select Priority</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
            {!formData.priority && (
              <div className="cart-configuration-edit-modal-placeholder">
                No priority set
              </div>
            )}
          </div>

          <div className="cart-configuration-edit-modal-form-group">
            <label>Estimated Leave Time (Today)</label>
            <input
              type="time"
              value={formData.leaveTime}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, leaveTime: e.target.value }))
              }
              placeholder="Select time"
            />
            {!formData.leaveTime && (
              <div className="cart-configuration-edit-modal-placeholder">
                No leave time set
              </div>
            )}
          </div>

          <div className="cart-configuration-edit-modal-form-group">
            <label>Assigned Gate</label>
            <select
              value={formData.assignedGate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  assignedGate: e.target.value,
                }))
              }
            >
              <option value="">Select Gate</option>
              {availableGates.map((gate) => (
                <option key={gate} value={gate}>
                  {gate}
                </option>
              ))}
            </select>
            {!formData.assignedGate && (
              <div className="cart-configuration-edit-modal-placeholder">
                No gate assigned
              </div>
            )}
          </div>

          <div className="cart-configuration-edit-modal-form-group">
            <label>Cart Type</label>
            <select
              value={formData.cartType}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, cartType: e.target.value }))
              }
            >
              <option value="">Select Type</option>
              {cartTypes.map((type) => (
                <option key={type.displayName} value={type.displayName}>
                  {type.displayName}
                </option>
              ))}
            </select>
            {!formData.cartType && (
              <div className="cart-configuration-edit-modal-placeholder">
                No type selected
              </div>
            )}
          </div>
        </div>

        <div className="cart-configuration-edit-modal-actions">
          <button
            onClick={handleSave}
            className="cart-configuration-edit-modal-btn-primary"
            disabled={isLoading}
          >
            <Save className="cart-configuration-edit-modal-icon-sm" />
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={onClose}
            className="cart-configuration-edit-modal-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartEditModal;
